<template>
  <div class="column-filter">
    <div v-if="loading" class="d-flex justify-content-center align-items-center py-4 h-100">
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </div>
    <div v-else>
      <span class="font-weight-bold">Filter</span>
      <v-divider color="#D7D7D7" class="mt-2"></v-divider>
      <div v-if="exportSelect === 'collection' || selectedExportReport.type === 'Report::Ticket'"
        style="background-color: #FFF2E0; border-radius: 4px;" class="mb-2 px-2 py-1">
        <span>Sample data remains unchanged because calculations are required.</span>
      </div>

      <div v-if="filteredFilters.length">
        <div v-for="filter in filteredFilters" :key="filter.name" class="mb-3">
          <label class="form-label">{{ getFilterLabel(filter) }}</label>

          <DynamicDateFilter v-if="filter.data_type === 'date'" :filter="filter"
            :radio-value="getDateFilterRadioValue(filter)"
            :presets="getDynamicPresetsForFilter(filter)"
            :dynamic-value="getDynamicFilterValue(filter)"
            :has-custom-config="Boolean(dynamicDateCustomConfig[filter.keys[0]])"
            :custom-value="getCustomDynamicValue(filter)"
            :custom-unit="getCustomDynamicUnit(filter)"
            :custom-max="getCustomDynamicMax(filter)"
            :unit-options="dynamicDateUnitOptions"
            :start-value="filterValues[filter.keys[0]]"
            :end-value="filter.keys[1] ? filterValues[filter.keys[1]] : ''"
            dynamic-description="Calculated from today's date for preview."
            note-text="Dynamic date filters are calculated from today's date for preview purposes."
            @radio-change="setDateFilterRadioValue"
            @preset-select="selectDynamicPreset"
            @custom-value-input="setCustomDynamicValue"
            @custom-unit-input="setCustomDynamicUnit"
            @start-date-input="$set(filterValues, filter.keys[0], $event)"
            @end-date-input="filter.keys[1] && $set(filterValues, filter.keys[1], $event)" />

          <!-- Multi-Checkbox -->
          <div v-else-if="filter.data_type === 'checkbox'">
            <!-- Grouped View (when parent has multiple values) -->
            <div v-if="Array.isArray(filter.data) && isGroupedFilter(filter)">
              <!-- Select All for grouped departments -->
              <div class="custom-control custom-checkbox mb-2">
                <input type="checkbox" :id="`${filter.keys[0]}-select-all`" :checked="isAllDepartmentsSelected(filter)"
                  @change="toggleAllDepartments(filter, $event.target.checked)" class="custom-control-input">
                <label :for="`${filter.keys[0]}-select-all`" class="custom-control-label font-weight-bold">
                  All
                </label>
              </div>
              <div v-for="(group, groupId) in getGroupedData(filter)" :key="groupId" class="mb-3"
                v-if="group.items && group.items.length > 0">
                <div class="parent-group-header"><b>{{ group.parentName }}</b></div>
                <div v-for="option in group.items" :key="option.id || option.name"
                  class="custom-control custom-checkbox ml-2">
                  <input type="checkbox" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                    v-model="filterValues[filter.keys[0]]" class="custom-control-input"
                    @change="handleCheckboxChange(filter.keys[0], option.id)">
                  <label :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label">
                    {{ option.name || '-' }}
                  </label>
                </div>
              </div>
            </div>
            <!-- Flat View (default) -->
            <div v-else-if="Array.isArray(filter.data)">
              <!-- Select All for Business Unit -->
              <div v-if="filter.keys[0] === businessUnitKey" class="custom-control custom-checkbox mb-2">
                <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                  :checked="isAllBusinessUnitsSelected(filter)"
                  @change="toggleAllBusinessUnits(filter, $event.target.checked)" class="custom-control-input">
                <label :for="`${filter.keys[0]}-select-all`" class="custom-control-label font-weight-bold">
                  All
                </label>
              </div>
              <!-- Select All for Department (when 1 BU selected - flat view) -->
              <div v-else-if="filter.keys[0] === departmentKey" class="custom-control custom-checkbox mb-2">
                <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                  :checked="isAllDepartmentsSelectedFlat(filter)"
                  @change="toggleAllDepartmentsFlat(filter, $event.target.checked)" class="custom-control-input">
                <label :for="`${filter.keys[0]}-select-all`" class="custom-control-label font-weight-bold">
                  All
                </label>
              </div>
              <div v-for="option in filteredData(filter)" :key="option.id || option.name"
                class="custom-control custom-checkbox"
                v-if="!(filter.keys[0] === businessUnitKey && option.id === 'all')">
                <input type="checkbox" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                  v-model="filterValues[filter.keys[0]]" class="custom-control-input"
                  @change="handleCheckboxChange(filter.keys[0], option.id)">
                <label v-if="filter.keys[0] === 'sticker_label_ids'" :for="`${filter.keys[0]}-${option.id}`"
                  class="custom-control-label sticker" :style="{ backgroundColor: option.color }">
                  {{ option.name || '-' }}
                </label>
                <label v-else-if="filter.keys[0] === 'template_ids' && option.id !== 'all'"
                  :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label"
                  :style="{ backgroundColor: option.color }">
                  #{{ option.id }} {{ option.name || '-' }}
                </label>
                <label v-else :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label">
                  {{ option.name || '-' }}
                </label>
              </div>
            </div>
            <v-autocomplete v-else v-model="filterValues[filter.keys[0]]" :items="dynamicOptions[filter.keys[0]]"
              :search-input.sync="searchInputs[filter.keys[0]]" :attach="true" item-value="id" item-text="name"
              label="Search" append-icon="mdi-magnify" solo dense flat hide-details hide-no-data multiple small-chips
              deletable-chips cache-items @update:search-input="fetchFilterData(filter.keys[0])"
              @change="searchInputs[filter.keys[0]] = ''"></v-autocomplete>
          </div>

          <!-- Select Dropdown -->
          <div v-else-if="filter.data_type === 'select'" class="position-relative">
            <v-icon class="search-icon position-absolute" style="top: 5px; right: 10px;">mdi-menu-down</v-icon>
            <select v-model="filterValues[filter.keys[0]]" class="form-control">
              <option v-for="option in filter.data" :key="option.id ? option.id : option"
                :value="option.id ? option.id : option" class="text-capitalize">
                {{ option.name_code_format ? option.name_code_format : option.name ? option.name : option }}
              </option>
            </select>
          </div>

          <!-- Radio Button Option -->
          <div v-else-if="filter.data_type === 'radio_button'">
            <div v-for="option in filter.data" :key="option.id ? option.id : option"
              class="custom-control custom-radio">
              <input type="radio" :id="`${filter.keys[0]}-${option.id ? option.id : option}`"
                :value="option.id ? option.id : option" v-model="filterValues[filter.keys[0]]"
                class="custom-control-input">
              <label :for="`${filter.keys[0]}-${option.id ? option.id : option}`" class="custom-control-label">
                {{ option.name ? option.name : option }}
              </label>
            </div>
          </div>
          <!-- Radio -->
          <div v-else-if="filter.data_type === 'radio'">
            <div v-for="option in filteredData(filter)"
              @click="handleRadioChecked(filter.keys[0], option.id ? option.id : option)"
              :key="option.id ? option.id : option" class="px-4 py-1 hover-light-blue" style="margin: 0 -16px 0 -16px;"
              :class="{
                'bg-blue-active': filterValues[filter.keys[0]] === (option.id ? option.id : option),
              }">
              <input type="radio" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                v-model="filterValues[filter.keys[0]]" style="opacity:1"
                @click="handleRadioChecked(filter.keys[0], option.id ? option.id : option)">
              {{ option.name ? option.name : option }}
            </div>

            <!-- custom start date -->
            <div v-if="filterValues[filter.keys[0]] == 'custom'" class="d-flex" style="gap: 16px;">
              <div>
                <label class="form-label mb-1" style="font-size: 12px;">Start Date</label>
                <CInput v-model="customStartDate" type="date" placeholder="mm/dd/yyyy"
                  style="width: 142px !important;" />
              </div>
              <div>
                <label class="form-label mb-1" style="font-size: 12px;">End Date</label>
                <CInput v-model="customEndDate" type="date" placeholder="mm/dd/yyyy" style="width: 142px !important;" />
              </div>
            </div>
          </div>

          <!-- Default Text Input -->
          <input v-else v-model="filterValues[filter.keys[0]]" type="text" class="form-control">
        </div>
      </div>

      <div class="d-flex action justify-content-between py-2 px-6 position-absolute"
        style="bottom: 0; right: 0; width: 100%;">
        <button class="text-primary" @click="clearFilters">Clear</button>
        <CButton :color="'#007BFF'" :disabled="disabledApplyFilter" label="Apply" @click.native="applyFilters">
        </CButton>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CButton from '../../../c-button.vue';
import _ from 'lodash';
import CInput from '../../../c-input.vue';
import DynamicDateFilter from '../../../report_filters/DynamicDateFilter.vue';
import dynamicDateFilterMixin from '../../../../shared/dynamic-date-filter-mixin';
import moment from 'moment';
import {
  isDynamicFilter,
  extractDynamicCustomConfig,
  resolveDynamicDateToAbsolute,
} from '../../../../shared/report-filter-helpers';

export default {
  components: { CButton, CInput, DynamicDateFilter },
  mixins: [dynamicDateFilterMixin],

  computed: {
    ...mapGetters({
      selectedExportReport: "reportList/selectedExportReport",
      appliedFilters: "reportList/appliedFilters",
      exportSelect: "reportList/exportSelect",
      reportFields: "reportList/reportFields",
      mergedFilters: 'reportList/mergedFilters',
      managementBusinessDepartment: "reportList/managementBusinessDepartment",
    }),
    isClientReport() {
      return this.selectedExportReport?.type === 'Report::Client';
    },
    businessUnitKey() {
      return this.isClientReport ? 'business_unit_id' : 'client_business_unit_id';
    },
    departmentKey() {
      return this.isClientReport ? 'department_id' : 'client_department_id';
    },
    disabledApplyFilter() {
      const isRadio = this.filters.some(item => item.data_type == "radio")
      const isCustom = Object.values(this.filterValues).some(value => value === "custom");
      const isCustomAndRadio = isRadio && isCustom
      const customDateDisabled = isCustomAndRadio && (this.customStartDate == '' || this.customEndDate == '')

      // Disable if BU is selected but department is empty
      const buValues = this.filterValues[this.businessUnitKey];
      const hasBuSelected = buValues && Array.isArray(buValues) && buValues.filter(id => id !== 'all').length > 0;
      const deptValues = this.filterValues[this.departmentKey];
      const hasDeptSelected = deptValues && Array.isArray(deptValues) && deptValues.length > 0;
      const deptRequiredButEmpty = hasBuSelected && !hasDeptSelected;

      const dateFilterInvalid = this.filters.filter(f => f.data_type === 'date').some(f => {
        const key = f.keys[0];
        const mode = this.dateFilterRadioValue[key];
        const config = this.dynamicDateCustomConfig[key];
        if (mode === 'dynamic' && config) return !config.value;
        if (mode === 'absolute') return !this.filterValues[key] || (f.keys.length > 1 && !this.filterValues[f.keys[1]]);
        return false;
      });

      return customDateDisabled || deptRequiredButEmpty || dateFilterInvalid;
    },
    filters() {
      if (this.exportSelect === 'collection') {
        return this.$store.state?.filterOptions || [];
      }
      return this.selectedExportReport?.filter_options || [];
    },
    filteredFilters() {
      return this.filters.filter(filter => {
        if (filter.data_type === 'date') return true;

        if (filter.keys.includes(this.departmentKey)) {
          // Hide department if no valid BU selected (exclude 'all' from valid selection)
          const buValues = this.filterValues[this.businessUnitKey];
          if (!buValues || !Array.isArray(buValues)) return false;
          const validBuIds = buValues.filter(id => id !== 'all');
          return validBuIds.length > 0;
        }
        if (filter.keys.includes(this.businessUnitKey)) {
          return this.managementBusinessDepartment;
        }
        return true;
      });
    },
    isQuestionnaireSelected() {
      const hasQuestionnaire = this.reportFields.some(col => col.category_key === 'Questionnaire::Template');
      const hasTemplateIds = this.filterValues.template_ids && this.filterValues.template_ids.length > 0;

      return hasQuestionnaire && hasTemplateIds;
    },
    selectedTemplateId() {
      const questionnaireColumn = this.reportFields.find(col => col.category_key === 'Questionnaire::Template');
      return questionnaireColumn ? questionnaireColumn.fieldable_id : null;
    },
  },

  data() {
    return {
      filterValues: {},
      searchInputs: {},
      dynamicOptions: {},
      loading: true,
      initialDeptLoaded: false,
      previousBuSelection: [],
      customStartDate: "",
      customEndDate: "",
    };
  },
  mounted() {

  },
  methods: {
    handleRadioChecked(filterKey, option) {
      if (this.filterValues[filterKey]) {
        this.filterValues[filterKey] = option
      }
    },
    async initializeFilterValues() {
      this.filterValues = {};
      this.dateFilterRadioValue = {};
      this.dynamicDateCustomConfig = {};
      this.dynamicDateFilterValues = this.normalizeDynamicFilters(this.mergedFilters?.dynamic_filters);

      // Initialize filterValues directly
      this.filterValues = { ...(this.mergedFilters || {}) };
      this.$delete(this.filterValues, 'dynamic_filters');

      this.filters.forEach(filter => {
        const savedFilterValue = this.filterValues[filter.keys[0]];

        if (filter.data_type === "checkbox") {
          if (Array.isArray(savedFilterValue) && savedFilterValue.length > 0 && !savedFilterValue.every(val => val === "")) {
            this.$set(this.filterValues, filter.keys[0], savedFilterValue);
          } else {
            const allOption = filter.data?.find(option => option.id === "all");
            this.$set(this.filterValues, filter.keys[0], allOption ? ["all"] : []);
          }

          const urlParams = new URLSearchParams(window.location.search);
          const hasTemplateIds = urlParams.has("template_ids[]");

          if (this.isQuestionnaireSelected && !hasTemplateIds) {
            this.$set(this.filterValues, 'template_ids', [this.selectedTemplateId]);
          }
        } else if (filter.data_type === "radio") {
          if (savedFilterValue) {
            if (typeof savedFilterValue === "string" && savedFilterValue.includes("to")) {
              const splitValueDate = savedFilterValue.split(" ")
              this.customStartDate = splitValueDate[0]
              this.customEndDate = splitValueDate[2]
              this.$set(this.filterValues, filter.keys[0], "custom");
            } else {
              this.$set(this.filterValues, filter.keys[0], savedFilterValue);
            }

          } else {
            const allOption = filter.data?.find(option => option.id === "all");
            this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
          }
        } else if (filter.data_type === "select" || filter.data_type === "radio_button") {
          if (savedFilterValue) {
            this.$set(this.filterValues, filter.keys[0], savedFilterValue);
          } else {
            const allOption = filter.data?.find(option => option.id === "all");
            this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
          }
        } else if (filter.data_type === "date" && filter.keys.length > 1) {
          const savedDynamicValue = this.getDynamicFilterValue(filter);
          if (savedDynamicValue) {
            this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
            const customConfig = extractDynamicCustomConfig(savedDynamicValue);
            if (customConfig) {
              this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
            }
            this.$set(this.filterValues, filter.keys[0], "");
            this.$set(this.filterValues, filter.keys[1], "");
          } else if (savedFilterValue) {
            if (isDynamicFilter(savedFilterValue)) {
              this.$set(this.dynamicDateFilterValues, filter.name, savedFilterValue);
              this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
              const customConfig = extractDynamicCustomConfig(savedFilterValue);
              if (customConfig) {
                this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
              }
              this.$set(this.filterValues, filter.keys[0], "");
              this.$set(this.filterValues, filter.keys[1], "");
            } else {
              this.$set(this.dateFilterRadioValue, filter.keys[0], 'absolute');
              this.$set(this.filterValues, filter.keys[0], savedFilterValue);
              this.$set(this.filterValues, filter.keys[1], this.filterValues[filter.keys[1]] || "");
            }
          } else {
            this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
            this.$set(this.filterValues, filter.keys[0], "");
            this.$set(this.filterValues, filter.keys[1], "");
          }
        } else if (filter.data_type === "date") {
          const savedDynamicValue = this.getDynamicFilterValue(filter);
          if (savedDynamicValue) {
            this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
            const customConfig = extractDynamicCustomConfig(savedDynamicValue);
            if (customConfig) {
              this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
            }
            this.$set(this.filterValues, filter.keys[0], "");
          } else if (savedFilterValue) {
            if (isDynamicFilter(savedFilterValue)) {
              this.$set(this.dynamicDateFilterValues, filter.name, savedFilterValue);
              this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
              const customConfig = extractDynamicCustomConfig(savedFilterValue);
              if (customConfig) {
                this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
              }
              this.$set(this.filterValues, filter.keys[0], "");
            } else {
              this.$set(this.dateFilterRadioValue, filter.keys[0], 'absolute');
              this.$set(this.filterValues, filter.keys[0], savedFilterValue);
            }
          } else {
            this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
            this.$set(this.filterValues, filter.keys[0], "");
          }
        } else {
          this.$set(this.filterValues, filter.keys[0], savedFilterValue ?? "");
        }

        if (!filter.data) {
          this.$set(this.dynamicOptions, filter.keys[0], []);
          this.$set(this.searchInputs, filter.keys[0], "");
        }
      });

      await this.fetchInitialFilterData();
      await this.fetchInitialFilterData();
      if (this.filterValues[this.businessUnitKey]) {
        this.handleBusinessUnitChange(this.filterValues[this.businessUnitKey]);
      }
      this.loading = false;
    },
    async fetchInitialFilterData() {
      try {
        const dynamicOptions = await this.$store.dispatch("reportList/fetchFilterData", { filters: this.selectedExportReport?.filter_options });
        this.dynamicOptions = dynamicOptions || {};
      } catch (error) {
        console.error("Error fetching initial filter data:", error);
      }
    },
    fetchFilterData: _.debounce(async function (key) {
      const query = this.searchInputs[key]?.trim();
      if (!query) return;

      try {
        const data = await this.$store.dispatch("reportList/fetchFilterData", { key, query });
        if (data) {
          this.$set(this.dynamicOptions, key, data[key]);
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
      }
    }, 500),
    filteredData(filter) {
      if (filter.keys[0] === this.departmentKey && Array.isArray(this.dynamicOptions[this.departmentKey])) {
        return this.dynamicOptions[this.departmentKey];
      }
      return filter.data;
    },
    isGroupedFilter(filter) {
      // Check if filter has parent config and parent has multiple values selected
      if (!filter.parent_key || !filter.parent_filter_key) return false;
      const parentValues = this.filterValues[filter.parent_filter_key];
      return Array.isArray(parentValues) && parentValues.length > 1;
    },
    getFilterLabel(filter) {
      if (['client_business_unit_id', 'client_department_id'].includes(filter.keys[0])) {
        return `${filter.name} (Client)`;
      }
      return filter.name;
    },
    getGroupedData(filter) {
      if (!filter.parent_key || !filter.parent_filter_key) return {};

      const parentFilterKey = filter.parent_filter_key;
      const parentKey = filter.parent_key;
      const parentValues = this.filterValues[parentFilterKey] || [];

      // Get parent filter to get parent names
      const parentFilter = this.filters.find(f => f.keys[0] === parentFilterKey);
      const parentNameMap = {};
      if (parentFilter && Array.isArray(parentFilter.data)) {
        parentFilter.data.forEach(item => {
          parentNameMap[item.id] = item.name;
        });
      }

      // Get data source (dynamic or static)
      let data;
      if (Array.isArray(this.dynamicOptions[filter.keys[0]])) {
        data = this.dynamicOptions[filter.keys[0]];
      } else {
        data = filter.data || [];
      }

      // Filter by selected parent values and exclude "all"
      const filteredData = data.filter(item =>
        item?.id !== "all" && parentValues.includes(item[parentKey])
      );

      // Group by parent key
      const grouped = {};
      filteredData.forEach(item => {
        const parentId = item[parentKey];
        if (!grouped[parentId]) {
          grouped[parentId] = {
            parentName: parentNameMap[parentId] || `Group ${parentId}`,
            items: []
          };
        }
        grouped[parentId].items.push(item);
      });
      return grouped;
    },
    isAllDepartmentsSelected(filter) {
      const groupedData = this.getGroupedData(filter);
      const allDeptIds = [];
      Object.values(groupedData).forEach(group => {
        group.items.forEach(item => allDeptIds.push(item.id));
      });
      const selectedIds = this.filterValues[filter.keys[0]] || [];
      return allDeptIds.length > 0 && allDeptIds.every(id => selectedIds.includes(id));
    },
    toggleAllDepartments(filter, checked) {
      const groupedData = this.getGroupedData(filter);
      const allDeptIds = [];
      Object.values(groupedData).forEach(group => {
        group.items.forEach(item => allDeptIds.push(item.id));
      });
      if (checked) {
        this.$set(this.filterValues, filter.keys[0], [...allDeptIds]);
      } else {
        this.$set(this.filterValues, filter.keys[0], []);
      }
    },
    isAllBusinessUnitsSelected(filter) {
      const data = filter.data || [];
      const allBuIds = data.filter(item => item.id !== 'all').map(item => item.id);
      const selectedIds = this.filterValues[filter.keys[0]] || [];
      return allBuIds.length > 0 && allBuIds.every(id => selectedIds.includes(id));
    },
    toggleAllBusinessUnits(filter, checked) {
      const data = filter.data || [];
      const allBuIds = data.filter(item => item.id !== 'all').map(item => item.id);
      if (checked) {
        this.$set(this.filterValues, filter.keys[0], [...allBuIds]);
      } else {
        this.$set(this.filterValues, filter.keys[0], []);
      }
    },
    // Methods for Department All checkbox in Flat View (when 1 BU selected)
    isAllDepartmentsSelectedFlat(filter) {
      const data = this.dynamicOptions[this.departmentKey] || [];
      const allDeptIds = data.filter(item => item.id !== 'all').map(item => item.id);
      const selectedIds = this.filterValues[filter.keys[0]] || [];
      return allDeptIds.length > 0 && allDeptIds.every(id => selectedIds.includes(id));
    },
    toggleAllDepartmentsFlat(filter, checked) {
      const data = this.dynamicOptions[this.departmentKey] || [];
      const allDeptIds = data.filter(item => item.id !== 'all').map(item => item.id);
      if (checked) {
        this.$set(this.filterValues, filter.keys[0], [...allDeptIds]);
      } else {
        this.$set(this.filterValues, filter.keys[0], []);
      }
    },
    handleCheckboxChange(filterKey, selectedId) {
      if (!this.filterValues[filterKey]) {
        this.$set(this.filterValues, filterKey, []);
      }

      let selectedValues = this.filterValues[filterKey];

      if (selectedId === "all") {
        this.$set(this.filterValues, filterKey, ["all"]);
      } else {
        this.filterValues[filterKey] = selectedValues.filter(id => id !== "all");
      }
    },
    applyFilters() {
      const filteredFilters = Object.fromEntries(
        Object.entries(this.filterValues).map(([key, value]) => {
          if (key === 'dynamic_filters') return null;
          if (Array.isArray(value)) {
            return [key, value.filter(v => v !== "all")];
          } else if (typeof value === "string") {
            if (value === "all") {
              if (key === this.businessUnitKey) {
                this.$set(this.filterValues, this.businessUnitKey, "");
                this.$set(this.filterValues, this.departmentKey, []);
                return [key, ""];
              }
              return [key, ""];
            } else if (value == "custom") {
              const startDateCustom = moment(this.customStartDate).format("YYYY-MM-DD")
              const endDateCustom = moment(this.customEndDate).format("YYYY-MM-DD")
              value = `${startDateCustom} to ${endDateCustom}`
            }

            if (value === "") {
              return [key, ""];
            }
          }
          return [key, value];
        }).filter(Boolean)
      );

      // Resolve dynamic date filters to absolute dates for preview
      this.filters.forEach(filter => {
        if (filter.data_type === 'date') {
          const dynamicValue = this.getDynamicFilterValue(filter);
          if (dynamicValue) {
            const { startDate, endDate } = resolveDynamicDateToAbsolute(dynamicValue, filter);
            if (filter.keys[0] && startDate) {
              filteredFilters[filter.keys[0]] = startDate;
            }
            if (filter.keys[1] && endDate) {
              filteredFilters[filter.keys[1]] = endDate;
            }
          }
        }
      });

      this.$store.commit('reportList/setAppliedFilters', filteredFilters);
      this.$store.commit('reportList/setBannerSuccess', 'Filters applied. Exported report will reflect the selected filters.');

      setTimeout(() => {
        this.$store.dispatch('reportList/getPreviewDataIds', { id: this.selectedExportReport?.id, params: filteredFilters });
        this.$store.dispatch('reportList/getDataCount', { id: this.selectedExportReport?.id, params: filteredFilters });
      }, 500)
      this.$emit("close-menu");
    },
    clearFilters() {
      this.filters.forEach(filter => {
        if (filter.data_type === "checkbox") {
          const allOption = filter.data?.find(option => option.id === "all");
          this.$set(this.filterValues, filter.keys[0], allOption ? ["all"] : []);
        } else if (filter.data_type === "radio_button" || filter.data_type === "select") {
          const allOption = filter.data?.find(option => option.id === "all");
          this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
        } else if (filter.data_type === "radio") {
          if (filter.keys[0] == "filed_date") {
            const savedFilterValue = this.filterValues[filter.keys[0]];
            this.$set(this.filterValues, filter.keys[0], "all");

            if (savedFilterValue) {
              if (typeof savedFilterValue === "string" && savedFilterValue == "custom") {
                this.$set(this.filterValues, filter.keys[0], "all");
                this.customStartDate = ""
                this.customEndDate = ""
              }
            }
          }
        } else if (filter.data_type === "date" && filter.keys.length > 1) {
          this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
          this.$set(this.filterValues, filter.keys[0], "");
          this.$set(this.filterValues, filter.keys[1], "");
          this.$delete(this.dynamicDateCustomConfig, filter.keys[0]);
          this.$delete(this.dynamicDateFilterValues, filter.name);
        } else if (filter.data_type === "date") {
          this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
          this.$set(this.filterValues, filter.keys[0], "");
          this.$delete(this.dynamicDateCustomConfig, filter.keys[0]);
          this.$delete(this.dynamicDateFilterValues, filter.name);
        } else {
          this.$set(this.filterValues, filter.keys[0], "");
        }
      });

      const modifiedFilters = Object.fromEntries(
        Object.entries(this.filterValues).map(([key, value]) => {
          if (key === 'dynamic_filters') return null;
          if (value === "all") {
            return [key, ""];
          } else if (Array.isArray(value)) {
            if (value.length === 0) {
              return [key, ""];
            } else if (value.length === 1 && value[0] === "") {
              return [key, [""]];
            } else {
              return [key, value.join(",")];
            }
          }
          return [key, value];
        }).filter(Boolean)
      );

      this.$store.commit('reportList/setAppliedFilters', modifiedFilters);
      this.$store.commit('reportList/setBannerSuccess', 'Filters cleared. Exported report will reflect the selected filters.');
      setTimeout(() => {
        this.$store.dispatch('reportList/getPreviewDataIds', {
          id: this.selectedExportReport?.id,
          params: modifiedFilters
        });
        this.$store.dispatch('reportList/getDataCount', { id: this.selectedExportReport?.id, params: modifiedFilters });
      }, 500);
      this.$emit("close-menu");
    },
    handleBusinessUnitChange(newBusinessUnitId, buKey, deptKey) {
      const departmentKeyToUse = deptKey || this.departmentKey;
      const departmentFilter = this.filters.find(f => f.keys[0] === departmentKeyToUse);
      if (!(departmentFilter && Array.isArray(departmentFilter.data))) return;

      const selectedBuIds = Array.isArray(newBusinessUnitId)
        ? newBusinessUnitId.filter(id => id !== 'all').map(id => id.toString())
        : [];

      if (selectedBuIds.length === 0) {
        this.$set(this.dynamicOptions, departmentKeyToUse, []);
        this.$set(this.filterValues, departmentKeyToUse, []);
        this.previousBuSelection = [];
        return;
      }

      const allDepartments = (departmentFilter.data || []).filter(dept => dept?.id !== 'all');
      const filteredDepartments = allDepartments.filter(dept =>
        selectedBuIds.includes((dept.business_unit_id || '').toString())
      );

      this.$set(this.dynamicOptions, departmentKeyToUse, filteredDepartments);

      const savedFilterValue = this.mergedFilters?.[departmentKeyToUse];
      if (!this.initialDeptLoaded && Array.isArray(savedFilterValue) && savedFilterValue.length > 0) {
        const validSavedDepartments = savedFilterValue.filter(id =>
          filteredDepartments.some(dept => dept.id === id)
        );
        if (validSavedDepartments.length > 0) {
          this.$set(this.filterValues, departmentKeyToUse, validSavedDepartments);
          this.initialDeptLoaded = true;
          this.previousBuSelection = [...selectedBuIds];
          return;
        }
      }

      const nextDeptIds = filteredDepartments.map(dept => dept.id);
      this.$set(this.filterValues, departmentKeyToUse, nextDeptIds);

      this.previousBuSelection = [...selectedBuIds];
      this.initialDeptLoaded = true;
    },
  },
  watch: {
    filters: {
      handler(newFilters) {
        if (newFilters && newFilters.length > 0) {
          this.initializeFilterValues();
        }
      },
      immediate: true
    },
    exportSelect(newValue) {
      if (newValue) {
        this.initializeFilterValues();
      }
    },
    "filterValues.client_business_unit_id": {
      handler(newVal) {
        // Only handle if this report uses client_business_unit_id (non-Client reports)
        if (!this.isClientReport) {
          this.handleBusinessUnitChange(newVal, 'client_business_unit_id', 'client_department_id');
        }
      },
      immediate: true
    },
    "filterValues.business_unit_id": {
      handler(newVal) {
        // Only handle if this report uses business_unit_id (Client reports)
        if (this.isClientReport) {
          this.handleBusinessUnitChange(newVal, 'business_unit_id', 'department_id');
        }
      },
      immediate: true
    },
    // Watch for mergedFilters changes to handle async data loading
    mergedFilters: {
      handler(newFilter) {
        if (!newFilter || this.initialDeptLoaded) return;

        const savedDeptIds = newFilter[this.departmentKey];
        if (Array.isArray(savedDeptIds) && savedDeptIds.length > 0) {
          const departmentFilter = this.filters.find(f => f.keys[0] === this.departmentKey);
          if (departmentFilter && Array.isArray(departmentFilter.data)) {
            const buValue = this.filterValues[this.businessUnitKey];
            const hasValidBu = buValue && Array.isArray(buValue) &&
              buValue.length > 0 && !buValue.includes('all');

            if (hasValidBu) {
              const filteredDepartments = departmentFilter.data.filter(dept =>
                dept?.id !== 'all' && buValue.includes(dept.business_unit_id)
              );
              const validSavedDepts = savedDeptIds.filter(id =>
                filteredDepartments.some(dept => dept.id === id)
              );
              if (validSavedDepts.length > 0) {
                this.$set(this.filterValues, this.departmentKey, validSavedDepts);
                this.initialDeptLoaded = true;
              }
            }
          }
        }
      },
      deep: true,
      immediate: true
    },
  }
};
</script>


<style lang="scss" scoped>
.column-filter {
  height: calc(100% - 38px);

  ::v-deep.v-list-item__action {
    margin-right: 10px !important;
  }

  .parent-group-header {
    font-weight: bold;
    font-size: 13px;
    color: #3F3F3F;
    padding: 8px 0 4px 0;
    border-bottom: 1px solid #E0E0E0;
    margin-bottom: 8px;
  }

  .sticker {
    padding: 8px 16px;
    border-radius: 4px;
    margin-bottom: 8px;
  }

  ::v-deep .custom-control-label.sticker::before,
  .custom-control-label.sticker::after {
    top: 0.7rem !important;
  }

  ::v-deep .v-select__selections {
    gap: 6px 0;
    padding: 8px 0;
  }

  .hover-light-blue {
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
      background-color: #F5F5F5;
    }
  }

  .bg-blue-active {
    background-color: #E3F2FD;
  }

}
</style>
